import { window as $window } from "global";
import { debounce, throttle } from "../../../util";

import { WaCxp } from "./cxp";

// Global Namespace: Cengage.WA.Question.CXP.Item

// The WaCxpItem class ...

// Notes: the code executes within the same iframe scope the CXP question content is within. Therefore, $window here is a reference to the global
// window object within the iframe

export type CxpItemOptions = {
    id: string;
};

export enum CxpVerbs {
    Regrade = "regrade",
    StatusUpdate = "wa-status-update",
}

export type CxpVerbsData = {
    id: string;
    data: any;
    verb: string;
};

export enum Events {
    Ready = "READY",
    Resize = "RESIZE",
    Touched = "TOUCHED",
    Verb = "VERB",
}

export type ReadyEventPayload = {};

export type ResizeEventPayload = {
    height: number;
    width: number;
};

export type TouchedEventPayload = {
    type: string;
};

export type VerbEventPayload = {
    id: string;
    data: string;
    verb: CxpVerbs;
};

export type WaCxpItemMessage = {
    payload:
        | ReadyEventPayload
        | ResizeEventPayload
        | TouchedEventPayload
        | VerbEventPayload;
    targetId: string;
    type: Events;
};

// height calculations can be fraction/decimal, so we use a fudge factor of 1px
const fudgeHeight = 1;
const fudgeWidth = 1;

//https://developer.mozilla.org/en-US/docs/Web/HTML/Element/iframe#height
const defaultIframeHeight = 150;

export class WaCxpItem {
    private answer: string | undefined; // the cached answer for the the CXP item
    CXP: WaCxp | undefined;
    cxpItem: JQuery<HTMLElement> | undefined;
    cxpItemOptions: CxpItemOptions | undefined;
    frameElement: HTMLIFrameElement | undefined; // the HTML element of the the iframe
    isMultiItem: boolean = false;
    private normalSpacingBetweenBodyAndDocument: number = 0;
    private triggeredResize: boolean = false;

    constructor(isPreload = false) {
        const frameElement = $window.frameElement;

        if (!frameElement) {
            console.warn(
                `We expected to be in an iframe, but no framing was detected.`,
            );
            return;
        }
        this.frameElement = frameElement;

        const waFrameContent =
            this.frameElement?.getAttribute("wa-frame-content");

        if (!waFrameContent) {
            const id = this.frameElement?.id;
            console.warn(
                `We expected a 'wa-frame-content' attribute on the iframe with id: '${id}', but the attribute is missing.`,
            );

            this.showErrorPage();

            return;
        }

        let cxpContent;

        try {
            cxpContent = $window.atob(waFrameContent);
        } catch (error) {
            const id = this.frameElement?.id;
            console.warn(
                `Invalid Base64 encoding for 'wa-frame-content' attribute on the iframe with id: '${id}'.`,
            );

            this.showErrorPage();

            return;
        }

        if (isPreload) {
            cxpContent = this.determinePreloadCxpContent(cxpContent);
        }

        $window.document.write(cxpContent);
    }

    // the purpose of preload is to figure how much of the CXP content can be loaded by the browser while the content is hidden
    // loading the content now should allow the browser to cache as many resources (e.g. CSS, JavaScripts, images, etc) as possible
    private determinePreloadCxpContent(htmlSource: string) {
        let preload = "<h3>Preload</h3>";
        // Create a new DOMParser
        const parser = new DOMParser();

        // Parse the HTML source string
        const doc = parser.parseFromString(htmlSource, "text/html");

        // Filter out script tags with src attributes
        let elements = doc.querySelectorAll("script[src]");

        elements.forEach(function (element) {
            preload += element.outerHTML;
        });

        // Filter out link tags with href attributes
        elements = doc.querySelectorAll("link[href]");

        elements.forEach(function (element) {
            preload += element.outerHTML;
        });

        // // Filter out video tags
        // elements = doc.getElementsByTagName("video");

        // for (const element of elements) {
        //     preload += element.outerHTML;
        // }

        // Filter out img tags with src attributes
        elements = doc.querySelectorAll("img[src]");

        elements.forEach(function (element) {
            preload += element.outerHTML;
        });

        for (const element of elements) {
            preload += element.outerHTML;
        }

        return preload;
    }

    // called from the inline JavaScript in frame.html
    initialize() {
        // should have access to CXP object now
        if (!$window.CXP) {
            const id = this.frameElement?.id;
            console.warn(
                `Unable to find global CXP object in the iframe scope for the iframe with id: '${id}'.`,
            );

            this.showErrorPage();

            return;
        }

        this.CXP = new WaCxp($window.CXP);

        // TODO: check for CXP init fails?

        // override CXP function to avoid errors
        // TODO: ask CXP team to fix their code
        $window.CXP._endBusy = $window.CXP.endBusy;
        $window.CXP.endBusy = (id: string | null) => {
            if (id === null) {
                return;
            }
            $window.CXP._endBusy(id);
        };

        // assumption: we can determine whether this is a multi item or not at this point (i.e. before setup is complete)
        if (this.CXP.isMultiItem()) {
            this.isMultiItem = true;
        }

        // request a callback from CXP when it is done loading/initializing
        if (this.isMultiItem) {
            // if we register a call for a multi item, then we will get a ready event for each individual sub item plus the parent
            // we try to filter to the 1 event we care about
            // Assumptions:
            // * the parent CXP item initializes last
            // * the parent CXP item has a CSS class of "covalent-item-multi"

            this.CXP.onSetupComplete((top, options) => {
                // the check for parent of multi item is copied from CXP.isMultiItem() in framework.js
                if (top.hasClass("covalent-item-multi")) {
                    this.handleCxpSetupComplete(top, options);
                }
            });
        } else {
            this.CXP.onSetupComplete((top, options) => {
                this.handleCxpSetupComplete(top, options);
            });
        }

        // Update the margin to align the content to the left side
        $window.document.body.style.marginLeft = "0";

        // we throttle events here since we don't need to react to every event with millisecond reaction time
        const throttledClick = throttle(
            (event: PointerEvent) => this.sendTouchedMessage(event),
            500,
        );

        const throttledKeyDown = throttle(
            (event: KeyboardEvent) => this.sendTouchedMessage(event),
            500,
        );

        const throttledKeyUp = throttle(
            (event: KeyboardEvent) => this.sendTouchedMessage(event),
            500,
        );

        const throttledMouseDown = throttle(
            (event: MouseEvent) => this.sendTouchedMessage(event),
            500,
        );

        const throttledMouseUp = throttle(
            (event: MouseEvent) => this.sendTouchedMessage(event),
            500,
        );

        const debouncedResize = debounce(() => {
            this.handleResize();
        }, 500);

        // add the debounced and throttled event listeners on events inside the iframe
        $window.addEventListener(
            "click",
            (event: PointerEvent) => throttledClick(event),
            false,
        );
        $window.addEventListener(
            "keydown",
            (event: KeyboardEvent) => throttledKeyDown(event),
            false,
        );
        $window.addEventListener(
            "keyup",
            (event: KeyboardEvent) => throttledKeyUp(event),
            false,
        );
        $window.addEventListener(
            "mouseup",
            (event: MouseEvent) => throttledMouseUp(event),
            false,
        );
        $window.addEventListener(
            "mousedown",
            (event: MouseEvent) => throttledMouseDown(event),
            false,
        );

        // add listener for post messages sent from CXP to this same window (i.e. within the same window) :/
        // (postMessage is typically meant for cross-frame communication)
        $window.addEventListener(
            "message",
            (event: MessageEvent) => this.handlePostMessage(event),
            false,
        );

        // watch for mutation events inside the iframe
        const mutationObserver = new MutationObserver(
            (mutationList, _observer) => {
                for (const mutation of mutationList) {
                    if (mutation.type === "childList") {
                        debouncedResize();
                    }
                }
            },
        );

        const config = {
            attributes: true,
            attributeOldValue: false,
            characterData: true,
            characterDataOldValue: false,
            childList: true,
            subtree: true,
        };

        mutationObserver.observe($window.document.body, config);
    }

    // called from the inline JavaScript in frame.html
    // this function runs right before the CXP content is written into the iframe
    pre() {
        const debouncedResize = debounce(() => {
            this.handleResize();
        }, 500);

        // add a resize event listener into the iframe 1st before any other resize listener
        $window.addEventListener(
            "resize",
            (event: Event) => {
                // keep the resize event from propagating to any CXP resize event listeners
                // we probably don't want any CXP resize events to trigger
                event.stopImmediatePropagation();
                if (!this.triggeredResize) {
                    debouncedResize();
                }
            },
            false,
        );
    }

    // called by WaCxpFrame
    getAnswerForBox(boxIndex: number) {
        let result = "";

        if (this.isMultiItem) {
            result = this.getRawAnswer();
            return result;
        }

        const answers = this.getRawAnswer();
        const parts = answers.split("&");
        result = parts[boxIndex];

        return result;
    }

    // called by WaCxpFrame
    getNumberOfBoxes() {
        let number = 0;

        if (this.CXP && this.cxpItem) {
            const subgrades = this.CXP.getCurrentGrade(this.cxpItem).subgrades;

            if (this.isMultiItem) {
                number = 1;
            } else {
                number = subgrades ? Object.keys(subgrades).length : 1;
            }
        }

        return number;
    }

    private getRawAnswer() {
        let result = "";

        if (!this.CXP) return result;

        // check for cached answer and return cached answer if found
        if (this.answer) {
            return this.answer;
        }

        if (this.isMultiItem) {
            const cssId = this.cxpItem?.attr("id");
            if (cssId) {
                result = this.CXP.getAnswerForMulti(cssId);
                this.answer = result;
                return result;
            }
        }

        if (!this.cxpItem) return result;

        result = this.CXP.getAnswer(this.cxpItem);
        this.answer = result;

        return result;
    }

    private handleCxpAnswerChanged(
        _answerField: JQuery<HTMLElement>,
        _answerValue: any,
        _details: any,
    ) {
        // clear the answer cache
        this.answer = "";
        // update the answer cache
        this.getRawAnswer();
    }

    private handleCxpSetupComplete(
        item: JQuery<HTMLElement>,
        options: CxpItemOptions,
    ) {
        this.cxpItem = item;
        this.cxpItemOptions = options;

        this.sendReadyMessage();

        // register an answer changed listener
        this.CXP?.onAnswerChanged(
            item,
            null,
            (answerField, answerValue, details) =>
                this.handleCxpAnswerChanged(answerField, answerValue, details),
            null,
        );
    }

    private handlePostMessage(event: MessageEvent) {
        const data = event.data;

        // we are only looking for post messages with verb in the event
        if (data?.verb) {
            this.sendVerbMessage(data);
        }
    }

    private handleResize() {
        const targetId = this.frameElement?.getAttribute("id");
        if (!targetId) return;

        let newWidth = $window.document.body.offsetWidth;
        let newHeight = $window.document.documentElement.scrollHeight;

        // If CXP content inside has smaller height than default iframe height,
        // then we need to resize it because we don't want to have a lot of whitespace.
        // In this case we need to take the height of the content
        // inside of iframe and hide the unnecessary vertical scroll bar.
        const htmlContent = $window.document.documentElement;
        if (htmlContent.offsetHeight < defaultIframeHeight) {
            newHeight = htmlContent.offsetHeight;
            htmlContent.style.overflow = "hidden";
        }

        // cache the "normal" spacing between the body and the document
        // use the cache value later when this spacing gets larger due to content wrapping
        if (!this.normalSpacingBetweenBodyAndDocument) {
            this.normalSpacingBetweenBodyAndDocument =
                $window.document.documentElement.scrollHeight -
                $window.document.body.scrollHeight;
        }

        if (
            $window.document.documentElement.scrollHeight -
                $window.document.documentElement.clientHeight <=
            1
        ) {
            const currentSpacingBetweenBodyAndDocument =
                $window.document.documentElement.scrollHeight -
                $window.document.body.scrollHeight;

            if (
                currentSpacingBetweenBodyAndDocument >
                this.normalSpacingBetweenBodyAndDocument
            ) {
                newHeight =
                    $window.document.body.scrollHeight +
                    this.normalSpacingBetweenBodyAndDocument;
            }
        }

        // we allow for a horizontal scrollbar for content that may overflow in the horizontal direction
        // a horizontal scrollbar may cause a vertical scrollbar to appear
        // check for horizontal overflow
        if (
            $window.document.documentElement.scrollWidth -
                $window.document.documentElement.clientWidth >
            fudgeWidth
        ) {
            // a horizontal scrollbar was detected
            // adjust the new height to account for the horizontal scrollbar (about 16px)
            newHeight += 16;
        }

        newHeight += fudgeHeight;

        const message = {
            payload: { height: newHeight, width: newWidth },
            targetId: targetId,
            type: Events.Resize,
        };

        this.triggeredResize = true;

        this.sendMessage(message);

        $window.setTimeout(() => {
            this.triggeredResize = false;
        }, 500);
    }

    // send post messages from the iframe to the parent page where a registered WaCxpFrame instance is listening
    private sendMessage(message: WaCxpItemMessage) {
        $window.parent.postMessage(message, $window.origin);
    }

    private sendReadyMessage() {
        // add an id to the post message so that the post message handlers can filter out messages not targeted to them
        const targetId = this.frameElement?.getAttribute("id");
        if (!targetId) return;

        const message = {
            payload: {},
            targetId: targetId,
            type: Events.Ready,
        };

        this.sendMessage(message);
    }

    private sendTouchedMessage(event: Event) {
        // add an id to the post message so that the post message handlers can filter out messages not targeted to them
        const targetId = this.frameElement?.getAttribute("id");
        if (!targetId) return;

        const message = {
            payload: {
                type: event.type,
            },
            targetId: targetId,
            type: Events.Touched,
        };

        this.sendMessage(message);
    }

    private sendVerbMessage(data: CxpVerbsData) {
        // add an id to the post message so that the post message handlers can filter out messages not targeted to them
        const targetId = this.frameElement?.getAttribute("id");
        if (!targetId) return;

        const message = {
            payload: {
                id: data.id,
                data: data.data,
                verb: data.verb,
            },
            targetId: targetId,
            type: Events.Verb,
        };

        this.sendMessage(message);
    }

    private showErrorPage() {
        // switch iframe source to error.html
        if (this.frameElement) {
            this.frameElement.src = `${__PATH__}error${__VERSION__}.html`;
        }
    }
}
